import sentinelLogo from "@/assets/images/Sentinel-Logo-Icon.png";
import {
    Button,
    ButtonGroup,
    DatePicker,
    Divider,
    Popover,
    PopoverContent,
    PopoverTrigger,
    Badge,
} from "@heroui/react";
import { Link, useRouter } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { NAV_BAR_OPTIONS } from "./nav";
import useAuth from "@/lib/auth/useAuth";
import {
    faClockRotateLeft,
    faMoneyCheckDollar,
    faQuestionCircle,
    faSave,
    faTrash,
    faFileLines,
    faBinoculars,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    DateValue,
    getLocalTimeZone,
    parseDate,
} from "@internationalized/date";
import useFeatureFlag from "@/lib/featureFlags/useFeatureFlags";
import dayjs from "dayjs";
import classNames from "classnames";
import { TourGuide } from "@/lib/tour/TourGuide";
import { useTour } from "@/lib/tour/useTour";
import useIsMobile from "@/lib/utils/useIsMobile";

const TOUR_START_DELAY = 500;
// import ScenarioPlannerIcon from "./ScenarioPlannerIcon";
import { AccessLevel } from "@/types/user";

function NavBar() {
    const router = useRouter();
    const { allNEnabled } = useFeatureFlag();
    const { userIsSentinel, user, isConsultant, forcedLevel, setForcedLevel, actionRequired } =
        useAuth();
    const { timeTravelEnabled } = useFeatureFlag();
    const [pathname, setPathname] = useState(router.state.location.pathname);
    const { hasSeenTour, startTour, resetTour } = useTour();
    const { isMobile } = useIsMobile();

    useEffect(() => {
        const unsubscribe = router.subscribe("onResolved", ({ toLocation }) => {
            setPathname(toLocation.pathname);
        });
        return unsubscribe;
    }, [router]);

    useEffect(() => {
        if (
            !isMobile &&
            !hasSeenTour &&
            (pathname === "/dashboard/home" || pathname === "/dashboard")
        ) {
            setTimeout(() => {
                startTour();
            }, TOUR_START_DELAY);
        }
    }, [pathname, startTour, hasSeenTour, isMobile]);

    const options = useMemo(() => {
        const options = [...NAV_BAR_OPTIONS];

        // if (userIsSentinel || allNEnabled) {
        //     options.splice(2, 0, {
        //         route: "/dashboard/all-n/planner",
        //         component: ({ color }: { color: string }) => (
        //             <ScenarioPlannerIcon color={color} />
        //         ),
        //         label: "Planner",
        //     });
        // }

        if (userIsSentinel) {
            options.splice(3, 0, {
                route: "/dashboard/documentation",
                component: ({ color }: { color: string }) => (
                    <FontAwesomeIcon
                        icon={faFileLines}
                        size="2x"
                        color={color}
                        className="w-[31px]"
                    />
                ),
                label: "Documentation",
            });
        }
        if (userIsSentinel || (user?.is_group_admin && isConsultant)) {
            const spliceIndex = userIsSentinel
                ? allNEnabled
                    ? 4
                    : 3
                : allNEnabled
                    ? 3
                    : 2;
            options.splice(spliceIndex, 0, {
                route: "/dashboard/enrollment",
                component: ({ color }: { color: string }) => (
                    <FontAwesomeIcon
                        icon={faMoneyCheckDollar}
                        size="2x"
                        color={color}
                        className="w-[31px]"
                    />
                ),
                label: "Enrollment",
            });
        }
        return options?.map((opt) => {
            const color = pathname.includes(opt.route) ? "#74BD43" : "#09426A";
            const tourAttribute =
                opt.label === "Home"
                    ? "home-icon"
                    : opt.label === "Fields"
                        ? "fields-icon"
                        : opt.label === "Planner"
                            ? "planner-icon"
                            : opt.label === "Documentation"
                                ? "documentation-icon"
                                : opt.label === "Network"
                                    ? "network-icon"
                                    : opt.label === "Enrollment"
                                        ? "enrollment-icon"
                                        : opt.label === "Settings"
                                            ? "settings-icon"
                                            : undefined;

            return (
                <Badge color="danger" isInvisible={opt.route !== "/dashboard/account-settings" || !actionRequired} content="">
                    <Button
                        key={opt.route}
                        isIconOnly
                        variant="light"
                        as={Link}
                        to={opt.route}
                        aria-label={opt.label}
                        data-tour={tourAttribute}
                    >
                        {opt.component({ color: opt.route === "/dashboard/account-settings" && actionRequired ? "red" : color })}
                    </Button>
                </Badge>
            );
        });
    }, [
        allNEnabled,
        isConsultant,
        pathname,
        user?.is_group_admin,
        userIsSentinel,
        actionRequired,
    ]);

    const [currentTimeTravelDate] = useState(
        window.sessionStorage.getItem("time-travel")
            ? dayjs(window.sessionStorage.getItem("time-travel"))
            : null,
    );

    const [value, setValue] = useState<DateValue | null>(
        window.sessionStorage.getItem("time-travel")
            ? parseDate(window.sessionStorage.getItem("time-travel") ?? "")
            : null,
    );

    const onSetTimeTravel = useCallback(() => {
        if (!value) return;
        const dateStr = dayjs(
            value?.toDate(getLocalTimeZone())?.toISOString(),
        ).format("YYYY-MM-DD");
        if (!dateStr) return;
        sessionStorage.setItem("time-travel", dateStr ?? "");
        window.location.reload();
    }, [value]);

    const onDeleteTimeTravel = useCallback(() => {
        sessionStorage.removeItem("time-travel");
        window.location.reload();
    }, []);

    return (
        <>
            <TourGuide />
            <div className="flex flex-col h-full bg-white w-14 border-r-[1px] border-r-gray-200 justify-between">
                <div>
                    <Link to="/dashboard">
                        <img src={sentinelLogo} className="w-full p-1 " />
                    </Link>
                    <Divider />
                    <div className="p-2 flex flex-col gap-2 ">{options}</div>
                </div>
                <div className="flex flex-col gap-2 items-center justify-self-center pb-4">
                    <Button
                        isIconOnly
                        variant="light"
                        onPress={() => {
                            resetTour();
                            setTimeout(() => {
                                startTour();
                            }, 300);
                        }}
                        aria-label="Start product tour"
                        className="hover:bg-gray-300 cursor-pointer rounded-md"
                    >
                        <FontAwesomeIcon
                            icon={faQuestionCircle}
                            size="2x"
                            className="w-[24px] text-primary-500"
                        />
                    </Button>
                    {userIsSentinel && (
                        <Popover showArrow placement="top-start">
                            <PopoverTrigger>
                                <div className="flex flex-col gap-1 items-center  hover:bg-gray-300 cursor-pointer rounded-md p-1">
                                    <FontAwesomeIcon
                                        icon={faBinoculars}
                                        size="2x"
                                        className={classNames({
                                            "w-[24px] text-primary-500": true,
                                        })}
                                    />
                                </div>
                            </PopoverTrigger>
                            <PopoverContent className="z-50">
                                <div className="flex flex-col gap-1 p-2">
                                    <div className=" font-semibold">
                                        Force View Level
                                    </div>
                                    <div className="flex flex-row gap-2 items-end">
                                        <ButtonGroup>
                                            <Button
                                                color={
                                                    forcedLevel ===
                                                        AccessLevel.SENTINEL
                                                        ? "primary"
                                                        : "default"
                                                }
                                                size="sm"
                                                onPress={() =>
                                                    setForcedLevel(
                                                        AccessLevel.SENTINEL,
                                                    )
                                                }
                                            >
                                                Sentinel
                                            </Button>
                                            <Button
                                                color={
                                                    forcedLevel ===
                                                        AccessLevel.DETAIL
                                                        ? "primary"
                                                        : "default"
                                                }
                                                size="sm"
                                                onPress={() =>
                                                    setForcedLevel(
                                                        AccessLevel.DETAIL,
                                                    )
                                                }
                                            >
                                                Detail (CSP)
                                            </Button>
                                            <Button
                                                color={
                                                    forcedLevel ===
                                                        AccessLevel.SUMMARY
                                                        ? "primary"
                                                        : "default"
                                                }
                                                size="sm"
                                                onPress={() =>
                                                    setForcedLevel(
                                                        AccessLevel.SUMMARY,
                                                    )
                                                }
                                            >
                                                Summary (Grower)
                                            </Button>
                                        </ButtonGroup>
                                        <Button
                                            color="danger"
                                            size="sm"
                                            onPress={() => setForcedLevel(null)}
                                        >
                                            Reset
                                        </Button>
                                    </div>
                                </div>
                            </PopoverContent>
                        </Popover>
                    )}
                    {timeTravelEnabled && (
                        <Popover showArrow placement="top-start">
                            <PopoverTrigger>
                                <div className="flex flex-col gap-1 items-center  hover:bg-gray-300 cursor-pointer rounded-md p-1">
                                    <FontAwesomeIcon
                                        icon={faClockRotateLeft}
                                        size="2x"
                                        className={classNames({
                                            "w-[24px] text-primary-500": true,
                                            "text-red-500":
                                                !!currentTimeTravelDate,
                                        })}
                                    />
                                    {currentTimeTravelDate && (
                                        <div className="text-[0.6rem] text-red-500 flex items-center flex-col">
                                            <span>
                                                {currentTimeTravelDate.format(
                                                    "MMM D",
                                                )}
                                            </span>
                                            <span>
                                                {currentTimeTravelDate.format(
                                                    "YYYY",
                                                )}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </PopoverTrigger>
                            <PopoverContent className="z-50">
                                <div className="flex flex-col gap-1 p-2">
                                    <div className=" font-semibold">
                                        Time Travel
                                    </div>
                                    <div>
                                        Set the simulated date. This will only
                                        impact the field dashboard for now.
                                    </div>
                                    <div className="flex flex-row gap-2 items-end">
                                        <DatePicker
                                            fullWidth
                                            size="sm"
                                            labelPlacement="outside"
                                            value={value}
                                            onChange={setValue}
                                            label="Time Travel"
                                            showMonthAndYearPickers
                                        />
                                        <Button
                                            color="primary"
                                            size="sm"
                                            startContent={
                                                <FontAwesomeIcon
                                                    icon={faSave}
                                                />
                                            }
                                            onPress={onSetTimeTravel}
                                        >
                                            Set
                                        </Button>
                                        {currentTimeTravelDate && (
                                            <Button
                                                color="danger"
                                                size="sm"
                                                startContent={
                                                    <FontAwesomeIcon
                                                        icon={faTrash}
                                                    />
                                                }
                                                onPress={onDeleteTimeTravel}
                                            >
                                                Clear
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </PopoverContent>
                        </Popover>
                    )}
                </div>
            </div>
        </>
    );
}

export default NavBar;
